// src/components/staffing/TeamOptimizationDashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Logger } from '../../utils/logger';
import { apiFetch } from '../../utils/api';
import { BRAND_PURPLE, COLOR_GOOD, COLOR_WARN, COLOR_POOR, TEXT_DISABLED, DOMAIN_COLORS, BG_ELEVATED, BG_SURFACE, BORDER_SECTION, BORDER_CARD, chipStyle, getScoreColor } from '../../utils/colors';
import EmployeeNameLink from '../common/EmployeeNameLink';
import { TABS } from '../cards/EmployeeExpandedView';
import { useEmployeeModal } from '../../contexts/EmployeeModalContext';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '@mui/material';
import {
  TrendingUp as PerformanceIcon,
  Psychology as InsightsIcon,
  Group as TeamIcon,
  Assessment as MetricsIcon,
  CheckCircle as SuccessIcon,
  Speed as SpeedIcon,
  AttachMoney as CostIcon,
  Security as RiskIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Share as ShareIcon,
  GetApp as GetAppIcon,
  AccessTime as TimingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Dashboard as SummaryIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Biotech as BiotechIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import ConciergeInsightCards from './ConciergeInsightCards';
import { parseConciergeInsights } from './helpers/conciergeInsightsParser';
import ClientIntroductionModal from './ClientIntroductionModal';
import ExecutiveSummary from './ExecutiveSummary';
import ConciergeInsights from './ConciergeInsights';
import ChemistryCompositeScore from './ChemistryCompositeScore';
import CollaborationNetwork from './CollaborationNetwork';
import DISCEngagementFit from './DISCEngagementFit';
import StrengthsFinderAlignment from './StrengthsFinderAlignment';
import DIRPlusMinusChart from './DIRPlusMinusChart';
import FinancialView from './FinancialView';
import TeamSynergy from './TeamSynergy';
import AvailabilityView from './AvailabilityView';
import { generateDetailedAnalysisPDF } from '../../utils/generateDocumentPDF';
const logger = new Logger('TeamOptimizationDashboard');

// Prevent duplicate auto-analyses when the component remounts (e.g. tab switches)
const _analysisInProgress = new Set();

const statusColor = (status) =>
  status === 'green' ? COLOR_GOOD : status === 'yellow' ? COLOR_WARN : COLOR_POOR;

const MitigationActionItem = ({ action }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Box sx={{ mb: 1, border: `1px solid ${COLOR_WARN}40`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{ p: 1.5, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', bgcolor: `${COLOR_WARN}08` }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{action.risk}</Typography>
        {expanded
          ? <ExpandLessIcon sx={{ color: TEXT_DISABLED, fontSize: 18, flexShrink: 0 }} />
          : <ExpandMoreIcon sx={{ color: TEXT_DISABLED, fontSize: 18, flexShrink: 0 }} />}
      </Box>
      {expanded && (
        <Box sx={{ p: 1.5, pt: 0.5 }}>
          <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.5 }}>{action.action}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            <Typography variant="caption"><span style={{ color: TEXT_DISABLED }}>Owner: </span>{action.owner}</Typography>
            <Typography variant="caption"><span style={{ color: TEXT_DISABLED }}>Timing: </span>{action.timing}</Typography>
          </Box>
          {action.success_indicator && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: COLOR_GOOD }}>
              ✓ {action.success_indicator}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// Returns true when the analysis JSON has overall_assessment = truncated/full synthesis JSON (backend fallback wrapping bug)
const isCorruptedAnalysis = (analysisText) => {
  if (!analysisText || typeof analysisText !== 'string') return false;
  try {
    const p = JSON.parse(analysisText);
    const oa = p?.summary?.overall_assessment;
    return typeof oa === 'string' && oa.trim().startsWith('{');
  } catch { return false; }
};

const renderStructuredAnalysis = (rawAnalysis) => {
  let parsed = null;
  try {
    let jsonText = typeof rawAnalysis === 'string' ? rawAnalysis : JSON.stringify(rawAnalysis);
    const fenceMatch = jsonText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (fenceMatch) jsonText = fenceMatch[1];
    let candidate = JSON.parse(jsonText);
    if (typeof candidate === 'string') candidate = JSON.parse(candidate);
    if (candidate && typeof candidate === 'object' && candidate.summary) {
      parsed = candidate;
    }
  } catch (e) {
    // parse failed — falls through to paragraph rendering
  }

  if (!parsed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {String(rawAnalysis).split(/\n\n+/).map((para, i) => (
          <Typography key={i} variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
            {para.trim()}
          </Typography>
        ))}
      </Box>
    );
  }

  const summary          = parsed.summary            || {};
  const scorecard        = parsed.scorecard           || [];
  const detailedSections = parsed.detailed_sections   || [];
  const tradeoffs        = parsed.key_tradeoffs       || [];
  const mitigationActions= parsed.mitigation_actions  || [];

  const BulletList = ({ items, color }) => (
    <Box>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, mt: 0.7, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {summary?.overall_assessment && (
        <Paper elevation={0} sx={{ p: 2, borderLeft: `3px solid ${BRAND_PURPLE}`, bgcolor: `${BRAND_PURPLE}10`, borderRadius: '0 8px 8px 0' }}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{summary.overall_assessment}</Typography>
        </Paper>
      )}

      {scorecard?.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: TEXT_DISABLED, textTransform: 'uppercase', letterSpacing: 1 }}>
            Score Summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {scorecard.map((item, i) => (
              <Tooltip key={i} title={item.summary || ''} arrow>
                <Chip
                  label={`${item.dimension} · ${item.score ?? item.ai_score ?? '—'}/10`}
                  size="small"
                  sx={{
                    bgcolor: `${statusColor(item.status)}20`,
                    color: statusColor(item.status),
                    border: `1px solid ${statusColor(item.status)}60`,
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {detailedSections?.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: TEXT_DISABLED, textTransform: 'uppercase', letterSpacing: 1 }}>
            Analysis
          </Typography>
          {detailedSections.map((section, i) => (
            <Accordion
              key={i}
              defaultExpanded={i === 0}
              sx={{ bgcolor: BG_ELEVATED, color: 'white', mb: 0.5, '&:before': { display: 'none' }, border: `1px solid ${BORDER_CARD}` }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: BRAND_PURPLE }} />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {section.content && (
                  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6, color: 'text.secondary' }}>
                    {section.content}
                  </Typography>
                )}
                {section.strengths?.length > 0 && <BulletList items={section.strengths} color={COLOR_GOOD} />}
                {section.concerns?.length > 0 && <Box sx={{ mt: section.strengths?.length ? 1 : 0 }}><BulletList items={section.concerns} color={COLOR_WARN} /></Box>}
                {section.recommendations?.length > 0 && <Box sx={{ mt: 1 }}><BulletList items={section.recommendations} color={BRAND_PURPLE} /></Box>}
                {!section.strengths?.length && !section.concerns?.length && !section.recommendations?.length && section.keyPoints?.length > 0 && (
                  <BulletList items={section.keyPoints} color={TEXT_DISABLED} />
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {tradeoffs?.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: TEXT_DISABLED, textTransform: 'uppercase', letterSpacing: 1 }}>
            Key Tradeoffs
          </Typography>
          {tradeoffs.map((t, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1, p: 1.5, bgcolor: `${COLOR_WARN}10`, border: `1px solid ${COLOR_WARN}30`, borderRadius: 1 }}>
              <Chip label={t.severity} size="small" sx={{ bgcolor: `${COLOR_WARN}20`, color: COLOR_WARN, fontSize: '0.65rem', height: 20, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{t.tradeoff}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {mitigationActions?.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: TEXT_DISABLED, textTransform: 'uppercase', letterSpacing: 1 }}>
            Mitigation Actions
          </Typography>
          {mitigationActions.map((action, i) => (
            <MitigationActionItem key={i} action={action} />
          ))}
        </Box>
      )}

    </Box>
  );
};

/**
 * ExecutiveSummaryTab — the default landing view after team analysis.
 * Shows hero score, strengths/tradeoffs, mitigation plan, recommendation.
 */
const ExecutiveSummaryTab = ({ overallScore, insights, isSynthesizing }) => {
  const score = overallScore || 0;
  const assessment =
    score >= 9 ? 'Exceptional' :
    score >= 8 ? 'Strong' :
    score >= 7 ? 'Good' :
    score >= 5 ? 'Adequate' : 'Marginal';
  const scoreColor =
    score >= 7 ? COLOR_GOOD :
    score >= 5 ? COLOR_WARN : COLOR_POOR;
  const interpretation =
    score >= 8 ? 'Staff this team with confidence.' :
    score >= 7 ? 'Staff with some tradeoffs to accept.' :
    score >= 5 ? 'Proceed with caution — address risks before committing.' :
    'Significant concerns — consider alternative candidates.';

  const strengths = insights?.summary?.strengths || [];
  const tradeoffs = insights?.tradeoffs || [];
  const mitigations = insights?.mitigationActions || [];
  const recommendation = insights?.summary?.assessment || '';

  const loadingPlaceholder = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
      <CircularProgress size={14} />
      <Typography variant="caption">Concierge analysis in progress…</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Section 1: Hero Score */}
      <Box sx={{ textAlign: 'center', py: 3, mb: 3, borderRadius: 2,
        background: `linear-gradient(135deg, ${scoreColor}10 0%, ${scoreColor}05 100%)`,
        border: `1px solid ${scoreColor}30` }}>
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Overall Team Score
        </Typography>
        <Typography sx={{ fontSize: '4rem', fontWeight: 900, color: scoreColor, lineHeight: 1 }}>
          {score.toFixed(1)}
        </Typography>
        <Typography variant="h6" sx={{ color: scoreColor, mt: 0.5, fontWeight: 'bold' }}>{assessment}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>{interpretation}</Typography>
      </Box>

      {/* Section 2: Strengths + Tradeoffs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: COLOR_GOOD }}>
            Top Strengths
          </Typography>
          {isSynthesizing && strengths.length === 0 ? loadingPlaceholder : (
            strengths.slice(0, 3).map((s, i) => (
              <Box key={i} sx={{ mb: 1, pl: 1.5, borderLeft: `3px solid ${COLOR_GOOD}`,
                bgcolor: `${COLOR_GOOD}0D`, borderRadius: '0 4px 4px 0', py: 0.75, pr: 1 }}>
                <Typography variant="body2">{s}</Typography>
              </Box>
            ))
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: COLOR_WARN }}>
            Key Tradeoffs
          </Typography>
          {isSynthesizing && tradeoffs.length === 0 ? loadingPlaceholder : (
            tradeoffs.slice(0, 3).map((t, i) => (
              <Box key={i} sx={{ mb: 1, pl: 1.5, borderLeft: `3px solid ${COLOR_WARN}`,
                bgcolor: `${COLOR_WARN}0D`, borderRadius: '0 4px 4px 0', py: 0.75, pr: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.tradeoff}</Typography>
                {t.severity && (
                  <Typography variant="caption" color="text.secondary">Severity: {t.severity}</Typography>
                )}
              </Box>
            ))
          )}
        </Grid>
      </Grid>

      {/* Section 3: Mitigation Action Plan */}
      {(mitigations.length > 0 || isSynthesizing) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Mitigation Action Plan
          </Typography>
          {isSynthesizing && mitigations.length === 0 ? loadingPlaceholder : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', fontSize: '0.7rem', color: 'text.secondary', py: 0.75 } }}>
                  <TableCell>#</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Timing</TableCell>
                  <TableCell>Success Indicator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mitigations.map((m, i) => (
                  <TableRow key={i} sx={{ verticalAlign: 'top',
                    borderLeft: `3px solid ${COLOR_WARN}`,
                    '& td': { fontSize: '0.7rem', py: 1, lineHeight: 1.4 } }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ color: COLOR_POOR, fontWeight: 500, maxWidth: 120 }}>{m.risk}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{m.action}</TableCell>
                    <TableCell sx={{ color: BRAND_PURPLE, whiteSpace: 'nowrap' }}>{m.owner}</TableCell>
                    <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap',
                      color: (m.timing||'').includes('kickoff') || (m.timing||'').includes('week') ? COLOR_POOR : COLOR_WARN }}>
                      {m.timing}
                    </TableCell>
                    <TableCell sx={{ color: COLOR_GOOD, maxWidth: 160 }}>{m.success_indicator}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Section 4: Recommendation */}
      {(recommendation || isSynthesizing) && (
        <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1, borderLeft: `4px solid ${BRAND_PURPLE}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.75, color: BRAND_PURPLE }}>
            Recommendation
          </Typography>
          {isSynthesizing && !recommendation ? loadingPlaceholder : (
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{recommendation}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * TechnicalCoverageTabContent - Sub-tabbed content for Individual and Team analysis
 */
const TechnicalCoverageTabContent = ({ components, teamSynergy, onEmployeeClick }) => {
  const [techSubTab, setTechSubTab] = useState(0);

  return (
    <Box>
      {/* Sub-tabs for Individual vs Team */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={techSubTab}
          onChange={(e, newValue) => setTechSubTab(newValue)}
          variant="fullWidth"
          sx={{ '& .MuiTab-root': { minHeight: '40px', fontSize: '0.8rem', fontWeight: 'medium' } }}
        >
          <Tab icon={<PersonIcon />} label="Individual Analysis" iconPosition="start" sx={{ gap: 1 }} />
          <Tab icon={<GroupsIcon />} label="Team Synergy" iconPosition="start" sx={{ gap: 1 }} />
        </Tabs>
      </Box>

      {/* Individual Analysis Sub-tab */}
      {techSubTab === 0 && (
        <Box>
          {/* Team Member Skills Coverage */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Team Member Skills Coverage
              </Typography>
              <Tooltip title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Individual Role Fit Score:</Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>• Breadth: % of required skills covered by this person</Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>• Depth: Average proficiency level in their skills</Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Combined score shows how well they match the role requirements
                  </Typography>
                </Box>
              } arrow placement="right">
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {components.technical_coverage?.individual_role_fits?.length > 0 ? (
              <Grid container spacing={2}>
                {components.technical_coverage.individual_role_fits.map((fit, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card sx={{ p: 2, bgcolor: BG_ELEVATED, border: `1px solid ${BRAND_PURPLE}33`, height: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                            <EmployeeNameLink employee={{ employee_id: fit.employee_id, name: fit.employee_name }} tab={TABS.ENGAGEMENTS} />
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {fit.assigned_role}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${fit.role_fit_score}`}
                          size="small"
                          sx={{ ...chipStyle(fit.role_fit_score >= 7 ? 'good' : fit.role_fit_score >= 5 ? 'warn' : 'poor'), fontWeight: 'bold' }}
                        />
                      </Box>

                      <Box sx={{ mb: 1.5 }}>
                        {/* Skills Breadth */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Skills Breadth</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {fit.skills_breadth_count !== undefined && fit.skills_breadth_total !== undefined
                                ? `${fit.skills_breadth_count}/${fit.skills_breadth_total}`
                                : fit.skills_breakdown
                                  ? `${fit.skills_breakdown.filter(s => (s.weighted_score ?? 0) > 0).length}/${fit.skills_breakdown.length}`
                                  : 'Calculating...'}
                            </Typography>
                          </Box>
                          {(() => {
                            const skills = fit.skills_breakdown || [];
                            const total = fit.skills_breadth_total || skills.length || 1;
                            const directCount = skills.filter(s => (s.weighted_score ?? 0) > 0 && (s.match_type === 'direct' || s.match_type === 'equivalent')).length;
                            const relatedCount = skills.filter(s => (s.weighted_score ?? 0) > 0 && s.match_type === 'related').length;
                            const gapCount = Math.max(0, total - directCount - relatedCount);
                            const directPct = (directCount / total) * 100;
                            const relatedPct = (relatedCount / total) * 100;
                            const gapPct = (gapCount / total) * 100;
                            return (
                              <>
                                <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.08)' }}>
                                  {directPct > 0 && <Box sx={{ width: `${directPct}%`, bgcolor: COLOR_GOOD, transition: 'width 0.3s' }} />}
                                  {relatedPct > 0 && <Box sx={{ width: `${relatedPct}%`, bgcolor: COLOR_WARN, transition: 'width 0.3s' }} />}
                                  {gapPct > 0 && <Box sx={{ width: `${gapPct}%`, bgcolor: COLOR_POOR, transition: 'width 0.3s' }} />}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.3 }}>
                                  {directCount > 0 && <Typography sx={{ fontSize: '0.62rem', color: COLOR_GOOD }}>{directCount} direct</Typography>}
                                  {relatedCount > 0 && <Typography sx={{ fontSize: '0.62rem', color: COLOR_WARN }}>{relatedCount} related</Typography>}
                                  {gapCount > 0 && <Typography sx={{ fontSize: '0.62rem', color: COLOR_POOR }}>{gapCount} gap{gapCount > 1 ? 's' : ''}</Typography>}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>

                        {/* Skills Depth */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Skills Depth</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {fit.skills_depth_average !== undefined
                                ? `${fit.skills_depth_average.toFixed(1)}/10`
                                : fit.skills_breakdown
                                  ? `${(fit.skills_breakdown.filter(s => s.weighted_score > 0).reduce((sum, s) => sum + s.weighted_score, 0) / Math.max(fit.skills_breakdown.filter(s => s.weighted_score > 0).length, 1)).toFixed(1)}/10`
                                  : 'N/A'}
                            </Typography>
                          </Box>
                          {(() => {
                            const depthScore = fit.skills_depth_average !== undefined
                              ? fit.skills_depth_average
                              : fit.skills_breakdown
                                ? fit.skills_breakdown.filter(s => s.weighted_score > 0).reduce((sum, s) => sum + s.weighted_score, 0) / Math.max(fit.skills_breakdown.filter(s => s.weighted_score > 0).length, 1)
                                : 0;
                            const depthColor = getScoreColor(depthScore);
                            return (
                              <LinearProgress
                                variant="determinate"
                                value={depthScore * 10}
                                sx={{ height: 6, borderRadius: 3, bgcolor: `${depthColor}1A`, '& .MuiLinearProgress-bar': { backgroundColor: depthColor } }}
                              />
                            );
                          })()}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.2, display: 'block' }}>
                            Average proficiency
                          </Typography>
                        </Box>
                      </Box>

                      {fit.skills_breakdown && (
                        <Box sx={{ mt: 1.5, borderTop: `1px solid rgba(255,255,255,0.08)`, pt: 1.5 }}>
                          {fit.skills_breakdown.map((skill, skillIndex) => {
                            const score = skill.weighted_score ?? skill.proficiency_score ?? 0;
                            const scoreColor = score === 0 ? COLOR_POOR : getScoreColor(score);
                            const isDirect = skill.match_type === 'direct';
                            const isEquivalent = skill.match_type === 'equivalent';
                            const isRelated = skill.match_type === 'related';
                            const skillDiffers = skill.employee_skill_name && skill.employee_skill_name.toLowerCase() !== skill.required_skill.toLowerCase();
                            const matchColor = isDirect || isEquivalent ? COLOR_GOOD : isRelated ? COLOR_WARN : COLOR_POOR;
                            const matchLabel = isDirect ? 'DIRECT' : isEquivalent ? 'EQUIV' : isRelated ? 'RELATED' : 'GAP';

                            return (
                              <Box
                                key={skillIndex}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  py: 0.4,
                                  borderBottom: skillIndex < fit.skills_breakdown.length - 1 ? `1px solid rgba(255,255,255,0.04)` : 'none',
                                  opacity: score === 0 ? 0.65 : 1,
                                }}
                              >
                                {/* Score */}
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: scoreColor,
                                    minWidth: '24px',
                                    textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {score === 0 ? '—' : score.toFixed(1)}
                                </Typography>

                                {/* Required skill name */}
                                <Typography
                                  sx={{
                                    fontSize: '0.72rem',
                                    flexGrow: 1,
                                    color: score === 0 ? COLOR_POOR : 'text.primary',
                                    textDecoration: score === 0 ? 'line-through' : 'none',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {skill.required_skill}
                                </Typography>

                                {/* "via employee skill" for non-direct matches */}
                                {skillDiffers && score > 0 && (
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                    via {skill.employee_skill_name}
                                  </Typography>
                                )}

                                {/* Match type badge */}
                                <Typography
                                  sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: 'bold',
                                    color: matchColor,
                                    border: `1px solid ${matchColor}40`,
                                    borderRadius: '3px',
                                    px: 0.5,
                                    py: 0.1,
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {matchLabel}
                                </Typography>

                                {/* Reasoning tooltip */}
                                {skill.reasoning && (
                                  <Tooltip title={skill.reasoning} arrow placement="left" enterDelay={300}>
                                    <InfoIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Individual role fit analysis will appear here after team optimization
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Team Synergy Sub-tab */}
      {techSubTab === 1 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <TeamSynergy data={teamSynergy} onEmployeeClick={onEmployeeClick} />
          </Box>

        </Box>
      )}
    </Box>
  );
};

/**
 * TeamOptimizationDashboard - Tabbed analysis of team composition
 */
const TeamOptimizationDashboard = ({
  staffingData,
  optimizationResult = null,
  onOptimizationUpdate,
  optimizationGoal = 'performance_maximization',
  loadedConfigurationId = null,
  loadedConfigurationName = null,
  teamAnalysisData = null,
  teamAnalysisLoading = false,
}) => {
  // State management
  const [localOptimizationResult, setLocalOptimizationResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [lastAnalyzedTeam, setLastAnalyzedTeam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedAnalysis, setDetailedAnalysis] = useState('');
  const [detailedAnalysisTimestamp, setDetailedAnalysisTimestamp] = useState(null);
  
  
  // Tab state for new tabbed layout
  const [activeTab, setActiveTab] = useState(0);
  
  // Performance prediction info dialog state
  const [performancePredictionInfoOpen, setPerformancePredictionInfoOpen] = useState(false);
  
  // Client introduction modal state
  const [clientIntroductionModalOpen, setClientIntroductionModalOpen] = useState(false);

  // Progressive loading state (must be at top level per React hooks rules)
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Phase 5: two-phase loading — scores load fast, synthesis runs in background
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  // Explicit flag: set to true when synthesis succeeds to trigger background enrichment
  const [needsEnrichment, setNeedsEnrichment] = useState(false);

  // Detailed analysis (Claude narrative) state
  const [isDetailedAnalysisLoading, setIsDetailedAnalysisLoading] = useState(false);
  const [isDetailedAnalysisPending, setIsDetailedAnalysisPending] = useState(false);
  const [detailedAnalysisError, setDetailedAnalysisError] = useState(null);

  // Client introduction background-generation state
  const [isClientIntroPending, setIsClientIntroPending] = useState(false);

  // DIR overrides — fetched from /dir endpoint per team member to fix backend 0.0 values
  const [dirOverrides, setDirOverrides] = useState({});

  // Derived from optimizationResult — defined early so functions defined below can reference
  // them without Terser creating a temporal dead zone in the minified bundle.
  const currentOptimizationResult = optimizationResult || localOptimizationResult;
  const overallScore = currentOptimizationResult?.overall_score || 0;
  const successProbability = currentOptimizationResult?.success_probability || 0;
  const components = currentOptimizationResult?.components || {};
  const claudeAnalysis = currentOptimizationResult?.claude_analysis || '';
  const agentScores = currentOptimizationResult?.agent_scores || {};
  const agentDetails = currentOptimizationResult?.agent_details || {};
  const technicalCoverage = components.technical_coverage?.score || 0;
  const teamChemistry = components.collaboration_score?.score || 0;
  const discCompatibility = components.disc_compatibility?.score || 0;
  const strengthsSynergy = components.strengths_synergy?.score || 0;
  const collaborationHistory = components.collaboration_history?.score || 0;
  const availabilityTiming = components.availability_timing?.score || 0;
  const timingAssessment = components.availability_timing?.assessment || 'Unknown';
  const riskAssessment = components.risk_assessment?.risk_level || 'medium';
  const experienceScore = (() => {
    const industryAvg = components.industry_client_experience?.industry_average || 7.5;
    const clientAvg = components.industry_client_experience?.client_average || 7.5;
    const collaborationScore = components.collaboration_history?.score || 0;
    const collaborationAvailable = components.collaboration_history?.data_available || false;
    const effectiveCollab = collaborationAvailable ? collaborationScore : ((industryAvg + clientAvg) / 2);
    return (clientAvg * 0.5) + (industryAvg * 0.3) + (effectiveCollab * 0.2);
  })();

  // Employee modal — name lookup for chart components that lack employee_id
  const { openEmployeeModal } = useEmployeeModal();

  const nameToEmployeeStub = React.useMemo(() => {
    const map = {};
    (staffingData?.roles || []).forEach(role => {
      if (role.assigned_employee_id && role.assigned_employee_name &&
          String(role.assigned_employee_id).startsWith('EMP')) {
        map[role.assigned_employee_name] = {
          employee_id: role.assigned_employee_id,
          name: role.assigned_employee_name,
        };
      }
    });
    return map;
  }, [staffingData?.roles]);

  const makeClickHandler = useCallback((tab) => (name) => {
    const emp = nameToEmployeeStub[name];
    if (emp) openEmployeeModal(emp, tab);
  }, [nameToEmployeeStub, openEmployeeModal]);

  // Fetch real DIR scores from /dir endpoint for each team member
  useEffect(() => {
    if (!teamAnalysisData || Object.keys(nameToEmployeeStub).length === 0) return;
    const entries = Object.values(nameToEmployeeStub);
    if (entries.length === 0) return;

    Promise.all(
      entries.map(({ employee_id, name }) =>
        apiFetch(`employees/${employee_id}/dir`)
          .then(r => r.ok ? r.json() : null)
          .then(data => data ? { name, data } : null)
          .catch(() => null)
      )
    ).then(results => {
      const overrides = {};
      results.forEach(r => {
        if (!r) return;
        const nd = r.data.normalized_dir || {};
        const conf = r.data.confidence || {};
        const projects = (r.data.project_details || []).length ||
                         (r.data.contribution_details?.project_ids || []).length || null;
        overrides[r.name] = {
          dir: nd.complete ?? null,
          projects,
          confidence: conf.complete ?? null,
        };
      });
      setDirOverrides(overrides);
    });
  }, [teamAnalysisData, nameToEmployeeStub]);

  // Calculate billing rate based on role level and name
  const calculateBillingRate = (roleName, roleLevel) => {
    // NO FALLBACKS - return null if no actual billing rate available
    // This function is kept for future use but returns null when data is unavailable
    return null;
  };

  // Get assigned team members from staffing data with real cost rates
  const getAssignedTeamMembers = async () => {
    if (!staffingData?.roles) return [];

    const assignedRoles = staffingData.roles.filter(role => role.status === 'assigned' && role.assigned_employee_id);

    // ✅ NEW: No longer fetching compensation here - backend fetches during enrichment and caches it!
    // Compensation data will be available in the optimization result from cache
    const teamMembers = assignedRoles.map((role) => {
      // 🔍 DEBUG: Console logs commented out for cleaner console
      // logger.debug('🔍 ANALYZE TEAM - Role data:', role.role_name, {
      //   billing_rate: role.billing_rate,
      //   hourly_rate: role.hourly_rate,
      //   assigned_hourly_rate: role.assigned_hourly_rate,
      //   total_hours: role.total_hours,
      //   role_object_keys: Object.keys(role)
      // });

      // ✅ FIX: Use billing rate from role data (what we charge the client)
      // This is already set in the role when it's created/assigned
      const actualBillingRate = role.billing_rate;

      // logger.debug('💰 BILLING RATE FOR', role.role_name, '=', actualBillingRate);

      if (!actualBillingRate) {
        logger.warn('⚠️ NO BILLING RATE FOUND FOR', role.role_name);
      }

      return {
        employee_id: role.assigned_employee_id,
        employee_name: role.assigned_employee_name,
        role: role.role_name,
        fit_score: role.fit_score || 8.0,
        assignment_confidence: 0.9,
        // ✅ FIXED: Use billing rate from role data (what we charge client)
        role_billing_rate: actualBillingRate,
        allocated_hours: role.total_hours || null,    // Actual allocated hours from role - NO FALLBACK
        // Keep original for backward compatibility (this will be overwritten by backend with employee cost)
        hourly_rate: null,  // Backend will fetch employee cost from cache
        // ✅ TIMING ANALYSIS: Include availability data for timing score calculation
        availability_reason: role.assigned_availability_reason,
        availability_tier: role.assigned_availability_tier,
        next_available_date: role.assigned_next_available_date
      };
    });

    logger.debug(`📋 Prepared ${teamMembers.length} team members for optimization (compensation will be fetched by backend)`);

    // ✅ DEBUG: Log availability data being sent to optimization API
    teamMembers.forEach(member => {
      logger.debug(`🔍 Team member ${member.employee_name}: availability_reason = '${member.availability_reason}'`);
    });

    return teamMembers;
  };

  // Check if team composition has changed
  const hasTeamChanged = async () => {
    const currentTeam = await getAssignedTeamMembers();
    if (!lastAnalyzedTeam || currentTeam.length !== lastAnalyzedTeam.length) {
      return true;
    }

    return currentTeam.some((member, index) =>
      member.employee_id !== lastAnalyzedTeam[index]?.employee_id
    );
  };

  // Auto-enhance existing configuration with optimization results
  const autoEnhanceExistingConfiguration = async () => {
    if (!loadedConfigurationId || !currentOptimizationResult) {
      logger.debug('🔍 No loaded configuration or optimization result to enhance');
      return;
    }

    try {
      logger.debug('🚀 Auto-enhancing existing configuration with optimization results:', loadedConfigurationName);

      const teamMembers = await getAssignedTeamMembers();

      // Calculate combined experience score: Role (70%) + Industry (15%) + Client (15%)
      const roleExpAvg = components.experience_balance?.average_role_experience_score || 7.0;
      const industryAvg = components.industry_client_experience?.industry_average || 7.5;
      const clientAvg = components.industry_client_experience?.client_average || 7.5;
      const calculatedExperienceScore = (roleExpAvg * 0.7) + (industryAvg * 0.15) + (clientAvg * 0.15);

      // ✅ FIX: Save the COMPLETE components object with all detailed data
      const enhancedOptimizationAnalysis = {
        overall_score: currentOptimizationResult?.overall_score || 0,
        technical_coverage: components.technical_coverage?.score || 0,
        experience_balance: calculatedExperienceScore, // Use calculated combined experience score
        collaboration_score: components.collaboration_score?.score || 0,
        disc_compatibility: components.disc_compatibility?.score || 0,
        strengths_synergy: components.strengths_synergy?.score || 0,
        risk_assessment: riskAssessment,
        predicted_success_rate: successProbability,
        confidence_interval: currentOptimizationResult?.confidence_interval || [0.0, 1.0],
        // ✅ CRITICAL: Save complete component details for restoration
        full_components: components
      };

      // Prepare enhanced comparison metrics
      const enhancedComparisonMetrics = {
        estimated_cost: components.margin_analysis?.total_cost || 0,
        estimated_revenue: components.margin_analysis?.total_revenue || 0,
        margin_percentage: components.margin_analysis?.overall_margin || 0,
        estimated_duration_weeks: components.margin_analysis?.project_duration_weeks || 12,
        default_hours_used: components.margin_analysis?.default_hours_used || false,
        risk_factors: components.risk_assessment?.identified_risks || [],
        strengths: ['Strong technical coverage', 'Good team chemistry', 'Optimized team composition'],
        skill_coverage_percentage: components.technical_coverage?.coverage_percentage || 0,
        team_chemistry_score: teamChemistry,
        performance_prediction: successProbability * 10
      };

      // Update the existing configuration with enhanced data
      const updatePayload = {
        team_members: teamMembers,
        optimization_analysis: enhancedOptimizationAnalysis,
        comparison_metrics: enhancedComparisonMetrics,
        estimated_revenue: components.margin_analysis?.total_revenue || 0,
        estimated_cost: components.margin_analysis?.total_cost || 0,
        margin_percentage: components.margin_analysis?.overall_margin || 0,
        default_hours_used: components.margin_analysis?.default_hours_used || false,
        claude_analysis: claudeAnalysis,
        detailed_analysis: detailedAnalysis || currentOptimizationResult?.detailed_analysis || claudeAnalysis, // Save full detailed analysis
        user_notes: `Enhanced with optimization analysis on ${new Date().toLocaleDateString()}`
      };

      // Use POST to update the existing configuration (backend handles update logic)
      const response = await apiFetch(
        'team-optimization/configurations',
        {
          method: 'POST',
          body: JSON.stringify({
            ...updatePayload,
            project_id: staffingData.id,
            configuration_name: loadedConfigurationName,
            optimization_goal: 'performance_maximization', // Default goal
            configuration_slot: null // Let backend determine slot for existing config
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.debug('⚠️ Failed to auto-enhance configuration:', errorData.message || response.status);
        return;
      }

      const data = await response.json();

      if (data.status === 'success') {
        logger.debug('✅ Configuration auto-enhanced successfully:', loadedConfigurationName);
      } else {
        logger.debug('⚠️ Failed to auto-enhance configuration:', data.message);
      }

    } catch (err) {
      logger.debug('⚠️ Error auto-enhancing configuration (non-critical):', err.message);
      // Don't show error to user since this is automatic enhancement
    }
  };

  // Stable refs so analyzeTeam can call background functions without
  // forward-reference closures that Terser's minifier turns into TDZ crashes.
  const runDetailedAnalysisBgRef = useRef(null);
  const runClientIntroBgRef = useRef(null);

  // Analyze team composition
  const analyzeTeam = async (forceRefresh = false) => {
    const teamMembers = await getAssignedTeamMembers();

    if (teamMembers.length === 0) {
      setLocalOptimizationResult(null);
      setError(null);
      return;
    }

    if (!forceRefresh && !(await hasTeamChanged()) && optimizationResult) {
      return; // No need to re-analyze
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      logger.debug('🎯 Analyzing team composition:', teamMembers);

      // Prepare project requirements
      const projectRequirements = {
        opportunity_id: staffingData?.id || staffingData?.opportunity_id,
        required_skills: [],
        critical_skills: [],
        required_experience_level: 'senior',
        estimated_duration_weeks: 12,
        hours_per_week: 40,
        industry: staffingData?.industry || 'Professional Services',
        client_id: staffingData?.clientId || staffingData?.client_id,
        client_name: staffingData?.clientName || staffingData?.client_name
      };

      // Extract skills from roles
      if (staffingData?.roles) {
        const allSkills = staffingData.roles.flatMap(role => role.skills || []);
        projectRequirements.required_skills = [...new Set(allSkills)];
        projectRequirements.critical_skills = allSkills.slice(0, 3);
      }

      // Phase 5: two-phase loading
      // Phase A — scores only (fast ~8s), page renders immediately
      const response = await apiFetch('team-optimization/optimize', {
        method: 'POST',
        body: JSON.stringify({
          team_members: teamMembers,
          project_requirements: projectRequirements,
          optimization_goal: optimizationGoal,
          skip_synthesis: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        const result = data.optimization_result;
        logger.debug('Team optimization scores loaded:', result);

        setLocalOptimizationResult(result);
        setLastAnalyzedTeam(teamMembers);

        // Phase B — synthesis runs while loading screen stays up (isAnalyzing still true)
        let synthSucceeded = false;
        setIsSynthesizing(true);
        try {
          const synthResponse = await apiFetch('team-optimization/synthesize', {
            method: 'POST',
            body: JSON.stringify({
              team_members: teamMembers,
              project_requirements: projectRequirements,
              optimization_goal: optimizationGoal
            })
          });
          if (synthResponse.ok) {
            const synthData = await synthResponse.json();
            if (synthData.status === 'success') {
              const merged = {
                ...result,
                claude_analysis: synthData.claude_analysis,
                detailed_analysis: synthData.detailed_analysis,
                agent_scores: synthData.agent_scores || result.agent_scores,
                agent_details: synthData.agent_details || {},
                synthesis_pending: false,
                _teamCompositionKey: teamCompositionKey,
              };
              synthSucceeded = true;
              setLocalOptimizationResult(merged);
              setDetailedAnalysis(synthData.detailed_analysis || synthData.claude_analysis || '');
              if (onOptimizationUpdate) onOptimizationUpdate(merged);
              setNeedsEnrichment(true);
            }
          }
        } catch (synthErr) {
          logger.error('Synthesis fetch error:', synthErr);
        } finally {
          setIsSynthesizing(false);
          // Background enrichment is triggered by the isSynthesizing useEffect below
        }

        // Auto-enhance existing configuration if one is loaded
        if (loadedConfigurationId && loadedConfigurationName) {
          setTimeout(() => { autoEnhanceExistingConfiguration(); }, 1000);
        }

        if (!synthSucceeded && onOptimizationUpdate) onOptimizationUpdate(result);
      } else {
        throw new Error(data.message || 'Optimization failed');
      }

    } catch (err) {
      logger.error('Team optimization error:', err);
      setError(`Failed to analyze team: ${err.message}`);
      setLocalOptimizationResult(null);
    } finally {
      setIsAnalyzing(false);
      // isSynthesizing is cleared in its own finally block above
    }
  };

  // Run synthesis only (no re-scoring) — used when loading a saved config with no analysis
  const handleSynthesizeOnly = async () => {
    if (!localOptimizationResult) return;
    setIsSynthesizing(true);
    try {
      const teamMembers = await getAssignedTeamMembers();
      const projectRequirements = {
        opportunity_id: staffingData?.id || staffingData?.opportunity_id,
        required_skills: staffingData?.roles?.flatMap(r => r.skills || []) || [],
        critical_skills: (staffingData?.roles?.flatMap(r => r.skills || []) || []).slice(0, 3),
      };
      const synthResponse = await apiFetch('team-optimization/synthesize', {
        method: 'POST',
        body: JSON.stringify({ team_members: teamMembers, project_requirements: projectRequirements, optimization_goal: optimizationGoal })
      });
      if (synthResponse.ok) {
        const synthData = await synthResponse.json();
        if (synthData.status === 'success') {
          const merged = {
            ...localOptimizationResult,
            claude_analysis: synthData.claude_analysis,
            detailed_analysis: synthData.detailed_analysis,
            agent_scores: synthData.agent_scores || localOptimizationResult.agent_scores,
            agent_details: synthData.agent_details || {},
            synthesis_pending: false,
          };
          setLocalOptimizationResult(merged);
          setDetailedAnalysis(synthData.detailed_analysis || synthData.claude_analysis || '');
          if (onOptimizationUpdate) onOptimizationUpdate(merged);
          setNeedsEnrichment(true);
        }
      }
    } catch (err) {
      logger.error('Synthesis-only error:', err);
    } finally {
      setIsSynthesizing(false);
      // Background enrichment is triggered by the isSynthesizing useEffect below
    }
  };

  // Auto-analyze disabled - user must manually click refresh to re-analyze
  // (Previously auto-analyzed on team changes, but this was disruptive during drag-and-drop)
  useEffect(() => {
    // Sync detailedAnalysis state with optimizationResult when it changes (e.g., from loading a config)
    logger.debug('🔥🔥🔥 USEEFFECT FIRED - optimizationResult:', optimizationResult);

    const syncOptimizationResult = async () => {
      if (optimizationResult) {
        logger.debug('🔥🔥🔥 HAS OPTIMIZATION RESULT');
        logger.debug('🎯 Syncing optimization result to dashboard', {
          hasDetailedAnalysis: !!optimizationResult.detailed_analysis,
          hasClaudeAnalysis: !!optimizationResult.claude_analysis,
          hasComponents: !!optimizationResult.components,
          detailedAnalysisLength: (optimizationResult.detailed_analysis || '').length,
          claudeAnalysisLength: (optimizationResult.claude_analysis || '').length
        });

        // CRITICAL: Set localOptimizationResult FIRST to display components immediately
        logger.debug('🔥🔥🔥 SETTING LOCAL OPTIMIZATION RESULT');
        setLocalOptimizationResult(optimizationResult);

        // Populate detailedAnalysis from loaded configuration
        // Try detailed_analysis first, then claude_analysis as fallback
        const analysisText = optimizationResult.detailed_analysis || optimizationResult.claude_analysis || '';
        logger.debug('🔥🔥🔥 ANALYSIS TEXT LENGTH:', analysisText.length);
        logger.debug('🔥🔥🔥 ANALYSIS TEXT PREVIEW:', analysisText.substring(0, 200));

        if (analysisText && analysisText.length > 0 && !isCorruptedAnalysis(analysisText)) {
          // Good data present — background fetch not needed
          logger.debug('🔥🔥🔥 SETTING DETAILED ANALYSIS (length:', analysisText.length, ')');
          logger.debug('✅ Setting detailed analysis text:', analysisText.substring(0, 100) + '...');
          setDetailedAnalysis(analysisText);

          // CRITICAL: Update lastAnalyzedTeam to prevent stale analysis clearing
          // When we load a saved configuration with analysis, mark it as "current"
          logger.debug('🔥🔥🔥 GETTING TEAM MEMBERS');
          const team = await getAssignedTeamMembers();
          logger.debug('🔥🔥🔥 SETTING LAST ANALYZED TEAM:', team.length, 'members');
          setLastAnalyzedTeam(team);
          logger.debug('✅ Updated lastAnalyzedTeam to match loaded configuration', {
            teamSize: team.length,
            teamMembers: team.map(m => m.employee_name)
          });
          logger.debug('🔥🔥🔥 SYNC COMPLETE - ANALYSIS SHOULD NOW BE VISIBLE');
        } else {
          logger.debug('🔥🔥🔥 NO ANALYSIS TEXT FOUND - checking what we have:');
          logger.debug('🔥🔥🔥   - detailed_analysis:', optimizationResult.detailed_analysis);
          logger.debug('🔥🔥🔥   - claude_analysis:', optimizationResult.claude_analysis);
          logger.warn('⚠️ No analysis text found in optimizationResult', {
            hasDetailedAnalysis: !!optimizationResult.detailed_analysis,
            hasClaudeAnalysis: !!optimizationResult.claude_analysis,
            detailedAnalysisType: typeof optimizationResult.detailed_analysis,
            claudeAnalysisType: typeof optimizationResult.claude_analysis
          });
          // No good analysis — start background fetch so button becomes active
          runDetailedAnalysisBgRef.current?.();
          setTimeout(() => runClientIntroBgRef.current?.(), 500);
        }
      } else {
        logger.debug('🔥🔥🔥 NO OPTIMIZATION RESULT - clearing analysis');
        logger.debug('🔄 optimizationResult is null/undefined');
      }
    };

    syncOptimizationResult();
  }, [optimizationResult]);

  // Fire background enrichment when synthesis explicitly succeeds.
  // needsEnrichment is set to true inside the synthesis success block — no transition
  // detection needed, no stale closure risk. Uses refs to avoid forward-reference issues.
  useEffect(() => {
    if (!needsEnrichment) return;
    setNeedsEnrichment(false);
    runDetailedAnalysisBgRef.current?.();
    setTimeout(() => runClientIntroBgRef.current?.(), 500);
  }, [needsEnrichment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track team composition for detecting changes
  const teamCompositionKey = React.useMemo(() => {
    if (!staffingData?.roles) return '';

    const assignedRoles = staffingData.roles
      .filter(role => role.status === 'assigned' && role.assigned_employee_id)
      .map(role => `${role.role_name}:${role.assigned_employee_id}`)
      .sort()
      .join('|');

    return assignedRoles;
  }, [staffingData?.roles]);

  // Clear stale analysis when team composition changes
  useEffect(() => {
    const checkTeamChange = async () => {
      const currentTeam = await getAssignedTeamMembers();

      // If we have analysis results but team has changed, clear stale data
      if ((detailedAnalysis || localOptimizationResult || optimizationResult) && lastAnalyzedTeam) {
        const teamChanged = currentTeam.length !== lastAnalyzedTeam.length ||
          currentTeam.some((member, index) =>
            member.employee_id !== lastAnalyzedTeam[index]?.employee_id
          );

        if (teamChanged) {
          logger.debug('🔄 Team composition changed - clearing stale analysis', {
            oldTeamSize: lastAnalyzedTeam.length,
            newTeamSize: currentTeam.length,
            teamCompositionKey,
            hadDetailedAnalysis: !!detailedAnalysis,
            hadLocalResult: !!localOptimizationResult,
            hadParentResult: !!optimizationResult
          });

          // Clear local state
          setDetailedAnalysis('');
          setLocalOptimizationResult(null);
          setLastAnalyzedTeam(null);

          // ✅ CRITICAL: Notify parent to clear its optimizationResult too
          if (onOptimizationUpdate) {
            onOptimizationUpdate(null);
          }

          logger.debug('✅ All analysis data cleared - parent notified');
        }
      }
    };

    checkTeamChange();
  }, [teamCompositionKey, onOptimizationUpdate]); // Use computed team composition key instead of roles array

  // Progressive loading animation effect
  useEffect(() => {
    if (!isAnalyzing && !isSynthesizing && !teamAnalysisLoading) return;

    // Phase 3: updated durations to match parallel agent architecture (~25-30s total)
    const steps = [
      { duration: 3000 },   // enriching data
      { duration: 5000 },   // technical coverage
      { duration: 12000 },  // parallel AI agents (chemistry/financial/availability/experience)
      { duration: 8000 },   // synthesis
      { duration: 2000 },   // finalizing
      { duration: 2000 }    // loop buffer
    ];

    let currentStep = 0;
    let stepStartTime = Date.now();

    const animationInterval = setInterval(() => {
      const elapsed = Date.now() - stepStartTime;
      const currentStepDuration = steps[currentStep]?.duration || 2000;
      const progress = Math.min((elapsed / currentStepDuration) * 100, 100);

      setLoadingProgress(progress);

      if (progress >= 100 && currentStep < steps.length - 1) {
        currentStep++;
        setLoadingStep(currentStep);
        stepStartTime = Date.now();
      } else if (currentStep >= steps.length - 1 && progress >= 100) {
        // Loop back to start if analysis is still running
        currentStep = 0;
        setLoadingStep(0);
        stepStartTime = Date.now();
      }
    }, 50);

    return () => clearInterval(animationInterval);
  }, [isAnalyzing, isSynthesizing, teamAnalysisLoading]);

  // Auto-trigger analysis on mount — component only mounts when tab 1 is active
  useEffect(() => {
    const opportunityId = staffingData?.id || staffingData?.opportunity_id;
    const hasTeam = staffingData?.roles?.some(r => r.status === 'assigned' && r.assigned_employee_id);
    // Stale: result exists but was built for a different team composition
    const isStale = optimizationResult?._teamCompositionKey &&
      optimizationResult._teamCompositionKey !== teamCompositionKey;
    const isComplete = staffingData?.progress?.completionPercentage === 100;
    if ((!optimizationResult || isStale) && hasTeam && isComplete && !_analysisInProgress.has(opportunityId)) {
      if (isStale) {
        setLocalOptimizationResult(null);
        if (onOptimizationUpdate) onOptimizationUpdate(null);
      }
      _analysisInProgress.add(opportunityId);
      analyzeTeam(false).finally(() => _analysisInProgress.delete(opportunityId));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live team analysis data — passed from parent (lifted to persist across tab switches) ──
  // Only use live data — never fall back to stale optimizer or prior-session values
  const td = teamAnalysisData;

  // Augment dirChart with real DIR values fetched from /dir endpoint
  const augmentedDirChart = React.useMemo(() => {
    const chart = td?.dirChart;
    if (!chart || Object.keys(dirOverrides).length === 0) return chart;
    const augmentedMembers = (chart.members || []).map(m => {
      const override = dirOverrides[m.name];
      if (!override || override.dir == null) return m;
      return {
        ...m,
        dir: override.dir,
        ...(override.projects != null ? { projects: override.projects } : {}),
        ...(override.confidence != null ? { confidence: override.confidence } : {}),
      };
    });
    const validDirs = augmentedMembers.map(m => m.dir).filter(d => d != null);
    const teamAvg = validDirs.length > 0
      ? validDirs.reduce((s, d) => s + d, 0) / validDirs.length
      : chart.teamAvg;
    return { ...chart, members: augmentedMembers, teamAvg };
  }, [td?.dirChart, dirOverrides]);

  // Render progressive loading state with status updates
  if (isAnalyzing || isSynthesizing || teamAnalysisLoading) {
    const loadingSteps = [
      { icon: '📊', label: 'Enriching team member data', color: BRAND_PURPLE },
      { icon: '🔍', label: 'Analyzing technical coverage', color: COLOR_GOOD },
      { icon: '🤖', label: 'Running parallel AI agents', color: BRAND_PURPLE },
      { icon: '✨', label: 'Synthesizing insights', color: COLOR_WARN },
      { icon: '💡', label: 'Preparing your report', color: '#00bcd4' },
      { icon: '🚀', label: 'Almost ready...', color: COLOR_POOR }
    ];

    const currentStepInfo = loadingSteps[loadingStep] || loadingSteps[0];

    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
        {/* Main spinner */}
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
          <CircularProgress
            size={80}
            thickness={3}
            sx={{ color: currentStepInfo.color }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h4">
              {currentStepInfo.icon}
            </Typography>
          </Box>
        </Box>

        {/* Current step label */}
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          {currentStepInfo.label}
        </Typography>

        {/* Step progress bar */}
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={loadingProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: currentStepInfo.color,
                borderRadius: 3,
              }
            }}
          />
        </Box>

        {/* Step indicators */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {loadingSteps.map((step, index) => (
            <Chip
              key={index}
              label={`${step.icon} ${step.label.split(' ')[0]}`}
              size="small"
              sx={{
                backgroundColor: index <= loadingStep ? step.color : 'rgba(0,0,0,0.08)',
                color: index <= loadingStep ? 'white' : 'text.secondary',
                fontWeight: index === loadingStep ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Check if we have assigned team members (synchronous check)
  const hasAssignedMembers = staffingData?.roles?.some(role => role.status === 'assigned' && role.assigned_employee_id);
  
  // Render empty state
  if (!hasAssignedMembers) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <TeamIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Team Members Assigned
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assign team members in the Team Assembly tab to see optimization analysis
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => analyzeTeam(true)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Render optimization results
  if (!currentOptimizationResult) {
    const hasTeam = staffingData?.roles?.some(r => r.status === 'assigned' && r.assigned_employee_id);
    if (hasTeam) {
      // Analysis will auto-start via useEffect — return null to avoid double-spinner flash
      return null;
    }
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={32} sx={{ color: BRAND_PURPLE }} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Analyzing team…
        </Typography>
      </Box>
    );
  }

  // (currentOptimizationResult and derived consts are defined near top of component)

  // ── Single source of truth for all tab label scores ─────────────────────
  // When td (live analysis) is available, use its scores everywhere so that
  // tab labels, hero circle, category cards, and Concierge all show the same number.
  // Scores are null until analysis runs — never fall back to optimizer values.
  const displayScores = {
    overall:      td?.scores?.overall      ?? null,
    technical:    td?.scores?.technical    ?? null,
    chemistry:    td?.scores?.chemistry    ?? null,
    financialPct: td?.financialView?.blendedMarginPct ?? null,
    financialNum: td?.financialView?.blendedMargin != null
                    ? td.financialView.blendedMargin * 100
                    : null,
    availability: td?.scores?.availability ?? null,
    experience:   td?.scores?.experience   ?? null,
  };

  // Function to extract complete summary section from detailed analysis
  const extractCompleteSummary = (detailedAnalysis) => {
    if (!detailedAnalysis) return '';
    
    // Look for the SUMMARY section in the detailed analysis
    const summaryMatch = detailedAnalysis.match(/## SUMMARY([\s\S]*?)(?=## DETAILED|$)/i);
    if (summaryMatch) {
      return `## SUMMARY${summaryMatch[1]}`.trim();
    }
    
    // Fallback: if no SUMMARY section found, return the first part up to DETAILED
    const detailedMatch = detailedAnalysis.match(/([\s\S]*?)(?=## DETAILED|$)/);
    if (detailedMatch) {
      return detailedMatch[1].trim();
    }
    
    return detailedAnalysis;
  };

  // Function to extract only the detailed section for the modal
  const extractDetailedSection = (detailedAnalysis) => {
    if (!detailedAnalysis) return '';
    
    logger.debug('🔍 Extracting detailed section from:', detailedAnalysis.substring(0, 200) + '...');
    
    // Look for the DETAILED ANALYSIS section
    const detailedMatch = detailedAnalysis.match(/## DETAILED ANALYSIS([\s\S]*)/i);
    if (detailedMatch) {
      const result = `## DETAILED ANALYSIS${detailedMatch[1]}`.trim();
      logger.debug('✅ Found DETAILED ANALYSIS section, length:', result.length);
      return result;
    }
    
    // Fallback: return everything after DETAILED if found
    const fallbackMatch = detailedAnalysis.match(/## DETAILED([\s\S]*)/i);
    if (fallbackMatch) {
      const result = `## DETAILED${fallbackMatch[1]}`.trim();
      logger.debug('✅ Found DETAILED section (fallback), length:', result.length);
      return result;
    }
    
    // If no DETAILED section found, return everything after the SUMMARY section
    const summaryMatch = detailedAnalysis.match(/## SUMMARY([\s\S]*?)(?=## |$)/i);
    if (summaryMatch) {
      // Find what comes after the summary
      const afterSummary = detailedAnalysis.substring(detailedAnalysis.indexOf(summaryMatch[0]) + summaryMatch[0].length);
      if (afterSummary.trim()) {
        logger.debug('✅ Using content after SUMMARY section, length:', afterSummary.length);
        return `## DETAILED ANALYSIS\n\n${afterSummary.trim()}`;
      }
    }
    
    // Final fallback: return the full analysis
    logger.debug('⚠️ No detailed section found, returning full analysis');
    return detailedAnalysis;
  };

  // Function to parse and render formatted Claude analysis
  const renderFormattedAnalysis = (analysisText, isModal = false) => {
    if (!analysisText) {
      return null;
    }

    // Simple approach: just clean up the text and display it nicely
    let cleanText = analysisText;
    
    // Remove redundant bold markdown
    cleanText = cleanText.replace(/\*\*(.*?)\*\*/g, '$1');
    
    // Convert bullet points to proper format
    cleanText = cleanText.replace(/^- /gm, '• ');
    
    return (
      <Typography 
        variant="body1" 
        sx={{ 
          whiteSpace: 'pre-wrap',
          lineHeight: 1.6,
          fontSize: '0.9rem',
          '& strong': {
            fontWeight: 'bold',
            color: BRAND_PURPLE
          }
        }}
      >
        {cleanText}
      </Typography>
    );
  };

  // Function to handle "Full analysis" click
  const handleFullAnalysisClick = () => {
    if (detailedAnalysis) {
      setIsModalOpen(true);
    }
  };

  // Runs detailed analysis silently in the background — dims the button until done
  const runDetailedAnalysisBackground = async () => {
    if (isDetailedAnalysisPending) return;
    const opportunityId = staffingData?.id || staffingData?.opportunity_id;
    const teamMembersForAnalysis = (staffingData?.roles || [])
      .filter(r => r.status === 'assigned' && r.assigned_employee_id)
      .map(r => ({ employeeId: r.assigned_employee_id, role: r.role_name }));
    if (!opportunityId || !teamMembersForAnalysis.length) return;

    setIsDetailedAnalysisPending(true);
    try {
      const res = await apiFetch('staffing/detailed-analysis', {
        method: 'POST',
        body: JSON.stringify({ opportunityId, teamMembers: teamMembersForAnalysis }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setDetailedAnalysis(data.analysis);
        if (data.cached_at) setDetailedAnalysisTimestamp(data.cached_at);
        if (onOptimizationUpdate) {
          const base = localOptimizationResult || optimizationResult || {};
          onOptimizationUpdate({ ...base, detailed_analysis: data.analysis, _teamCompositionKey: teamCompositionKey });
        }
      }
    } catch (err) {
      logger.error('Background detailed analysis error:', err);
    } finally {
      setIsDetailedAnalysisPending(false);
    }
  };

  runDetailedAnalysisBgRef.current = runDetailedAnalysisBackground;

  // Generates client introduction silently in background — dims the button until done
  const runClientIntroBackground = async () => {
    if (isClientIntroPending) return;
    const existingIntro = (localOptimizationResult || optimizationResult)?.client_introduction;
    if (existingIntro) return; // already have one — no need to regenerate
    const opportunityId = staffingData?.id || staffingData?.opportunity_id;
    const hasTeam = (staffingData?.roles || []).some(r => r.status === 'assigned' && r.assigned_employee_id);
    if (!opportunityId || !hasTeam) return;

    setIsClientIntroPending(true);
    try {
      const res = await apiFetch('team-optimization/client-introduction', {
        method: 'POST',
        body: JSON.stringify({
          team_optimization_result: localOptimizationResult || optimizationResult,
          project_context: {
            project_name: staffingData?.projectName || staffingData?.name || 'Team Analysis Project',
            client_name: staffingData?.clientName || 'Client',
            opportunity_id: opportunityId,
          },
          client_context: {
            client_name: staffingData?.clientName || 'Client',
            industry: staffingData?.industry || 'Professional Services',
            engagement_type: 'Team Analysis & Consulting',
          },
        }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (onOptimizationUpdate) {
          const base = localOptimizationResult || optimizationResult || {};
          onOptimizationUpdate({ ...base, client_introduction: data.document, _teamCompositionKey: teamCompositionKey });
        }
      }
    } catch (err) {
      logger.error('Background client introduction error:', err);
    } finally {
      setIsClientIntroPending(false);
    }
  };

  runClientIntroBgRef.current = runClientIntroBackground;

  // Force-regenerate: bypass cache and generate fresh analysis
  const handleRegenerateAnalysis = async () => {
    setDetailedAnalysis('');
    setDetailedAnalysisTimestamp(null);
    setIsDetailedAnalysisLoading(true);
    setDetailedAnalysisError(null);
    try {
      const opportunityId = staffingData?.id || staffingData?.opportunity_id;
      const teamMembersForAnalysis = (staffingData?.roles || [])
        .filter(r => r.status === 'assigned' && r.assigned_employee_id)
        .map(r => ({ employeeId: r.assigned_employee_id, role: r.role_name }));
      const res = await apiFetch('staffing/detailed-analysis', {
        method: 'POST',
        body: JSON.stringify({ opportunityId, teamMembers: teamMembersForAnalysis, forceRefresh: true }),
      });
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || `HTTP ${res.status}`);
      if (data.status === 'success') {
        setDetailedAnalysis(data.analysis);
        if (data.cached_at) setDetailedAnalysisTimestamp(data.cached_at);
        if (onOptimizationUpdate) {
          const base = localOptimizationResult || optimizationResult || {};
          onOptimizationUpdate({ ...base, detailed_analysis: data.analysis });
        }
      }
    } catch (err) {
      logger.error('Regenerate detailed analysis error:', err);
      setDetailedAnalysisError(err.message || 'Unknown error');
    } finally {
      setIsDetailedAnalysisLoading(false);
    }
  };

  // Button click handler — opens modal if ready, falls back to inline fetch if needed
  const runDetailedAnalysis = async () => {
    setIsModalOpen(true);
    if (detailedAnalysis && !isCorruptedAnalysis(detailedAnalysis)) return;
    if (isDetailedAnalysisPending) return; // modal shows spinner while background finishes

    // Fallback: background fetch didn't run or failed — fetch inline
    if (detailedAnalysis) setDetailedAnalysis('');
    setIsDetailedAnalysisLoading(true);
    setDetailedAnalysisError(null);
    try {
      const opportunityId = staffingData?.id || staffingData?.opportunity_id;
      const teamMembersForAnalysis = (staffingData?.roles || [])
        .filter(r => r.status === 'assigned' && r.assigned_employee_id)
        .map(r => ({ employeeId: r.assigned_employee_id, role: r.role_name }));
      const res = await apiFetch('staffing/detailed-analysis', {
        method: 'POST',
        body: JSON.stringify({ opportunityId, teamMembers: teamMembersForAnalysis }),
      });
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || `HTTP ${res.status}`);
      if (data.status === 'success') {
        setDetailedAnalysis(data.analysis);
        if (data.cached_at) setDetailedAnalysisTimestamp(data.cached_at);
        if (onOptimizationUpdate) {
          const base = localOptimizationResult || optimizationResult || {};
          onOptimizationUpdate({ ...base, detailed_analysis: data.analysis });
        }
      }
    } catch (err) {
      logger.error('Detailed analysis error:', err);
      setDetailedAnalysisError(err.message || 'Unknown error');
    } finally {
      setIsDetailedAnalysisLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with inline performance score */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Team Analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(() => {
            const filledRoles = (staffingData?.roles || []).filter(r => r.status === 'assigned' && r.assigned_employee_id).length;
            const totalRoles = staffingData?.roles?.length || 0;
            return (
              <Chip
                label={`${filledRoles}/${totalRoles} roles`}
                size="small"
                color={filledRoles === totalRoles && totalRoles > 0 ? 'success' : 'warning'}
                variant="outlined"
              />
            );
          })()}
          {currentOptimizationResult && (
            <Tooltip title={isClientIntroPending ? 'Preparing introduction...' : ''} arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isClientIntroPending
                    ? <CircularProgress size={14} sx={{ color: TEXT_DISABLED }} />
                    : <InsightsIcon />}
                  onClick={() => setClientIntroductionModalOpen(true)}
                  disabled={isClientIntroPending}
                  sx={{
                    borderColor: isClientIntroPending ? TEXT_DISABLED : '#BB86FC',
                    color: isClientIntroPending ? TEXT_DISABLED : '#BB86FC',
                    '&:hover': {
                      borderColor: '#9C4DCC',
                      color: '#9C4DCC',
                      bgcolor: 'rgba(187, 134, 252, 0.04)'
                    },
                    '&:disabled': { borderColor: TEXT_DISABLED, color: TEXT_DISABLED },
                    fontSize: '0.75rem',
                    px: 2
                  }}
                >
                  Client Introduction
                </Button>
              </span>
            </Tooltip>
          )}
          {currentOptimizationResult && (
            <>
              <Tooltip title={isDetailedAnalysisPending ? 'Preparing analysis...' : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={(isDetailedAnalysisPending || isDetailedAnalysisLoading)
                      ? <CircularProgress size={14} sx={{ color: 'white' }} />
                      : <InsightsIcon />}
                    onClick={runDetailedAnalysis}
                    disabled={isDetailedAnalysisPending || isDetailedAnalysisLoading}
                    sx={{
                      bgcolor: (isDetailedAnalysisPending || isDetailedAnalysisLoading) ? TEXT_DISABLED : BRAND_PURPLE,
                      '&:hover': { bgcolor: BRAND_PURPLE },
                      '&:disabled': { bgcolor: TEXT_DISABLED, color: 'white' },
                      fontSize: '0.75rem',
                      px: 2
                    }}
                  >
                    View Detailed Analysis
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Refresh Analysis">
            <IconButton onClick={() => analyzeTeam(true)} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* New Layout: Left Tabbed Content + Right Concierge */}
      <Grid container spacing={3}>
        {/* Left Column - Tabbed Content (Narrower) */}
        <Grid item xs={12} md={8}>
          <Card sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Tab Headers */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: '58px',
                    fontSize: '1.09rem',
                    fontWeight: 'medium'
                  }
                }}
              >
                {/* Tab 0: Executive Summary (default) */}
                <Tab
                  icon={<SummaryIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        SUMMARY
                      </Typography>
                      {displayScores.overall != null && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                          {displayScores.overall.toFixed(1)}
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.overall == null ? BRAND_PURPLE : getScoreColor(displayScores.overall),
                    '&.Mui-selected': {
                      color: displayScores.overall == null ? BRAND_PURPLE : getScoreColor(displayScores.overall)
                    }
                  }}
                />
                {/* Tab 1: Technical Coverage */}
                <Tab
                  icon={<SettingsIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        TECHNICAL COVERAGE
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                        {displayScores.technical != null ? displayScores.technical.toFixed(1) : '—'}
                      </Typography>
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.technical == null ? TEXT_DISABLED : getScoreColor(displayScores.technical),
                    '&.Mui-selected': {
                      color: displayScores.technical == null ? TEXT_DISABLED : getScoreColor(displayScores.technical)
                    }
                  }}
                />
                <Tab
                  icon={<BiotechIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        TEAM CHEMISTRY
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                        {displayScores.chemistry != null ? displayScores.chemistry.toFixed(1) : '—'}
                      </Typography>
                      {agentScores.chemistry_score != null && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.8125rem', opacity: 0.75 }}>
                          AI: {agentScores.chemistry_score}%
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.chemistry == null ? TEXT_DISABLED : getScoreColor(displayScores.chemistry),
                    '&.Mui-selected': {
                      color: displayScores.chemistry == null ? TEXT_DISABLED : getScoreColor(displayScores.chemistry)
                    }
                  }}
                />
                <Tab
                  icon={<CostIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        FINANCIAL
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                        {displayScores.financialPct ?? '—'}
                      </Typography>
                      {agentScores.financial_score != null && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.8125rem', opacity: 0.75 }}>
                          AI: {agentScores.financial_score}%
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.financialNum == null ? TEXT_DISABLED : displayScores.financialNum >= 50 ? COLOR_GOOD : displayScores.financialNum >= 30 ? COLOR_WARN : COLOR_POOR,
                    '&.Mui-selected': {
                      color: displayScores.financialNum == null ? TEXT_DISABLED : displayScores.financialNum >= 50 ? COLOR_GOOD : displayScores.financialNum >= 30 ? COLOR_WARN : COLOR_POOR
                    }
                  }}
                />
                <Tab
                  icon={<TimingIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        AVAILABILITY
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                        {displayScores.availability != null ? displayScores.availability.toFixed(1) : '—'}
                      </Typography>
                      {agentScores.availability_score != null && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.8125rem', opacity: 0.75 }}>
                          AI: {agentScores.availability_score}%
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.availability == null ? TEXT_DISABLED : getScoreColor(displayScores.availability),
                    '&.Mui-selected': {
                      color: displayScores.availability == null ? TEXT_DISABLED : getScoreColor(displayScores.availability)
                    }
                  }}
                />
                <Tab
                  icon={<StarIcon />}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 'medium' }}>
                        EXPERIENCE
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '1.0rem', fontWeight: 'bold' }}>
                        {displayScores.experience != null ? displayScores.experience.toFixed(1) : '—'}
                      </Typography>
                      {agentScores.experience_score != null && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.8125rem', opacity: 0.75 }}>
                          AI: {agentScores.experience_score}%
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    gap: 1,
                    color: displayScores.experience == null ? TEXT_DISABLED : getScoreColor(displayScores.experience),
                    '&.Mui-selected': {
                      color: displayScores.experience == null ? TEXT_DISABLED : getScoreColor(displayScores.experience)
                    }
                  }}
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              {/* Tab 0: Executive Summary — wait for live analysis data */}
              {activeTab === 0 && teamAnalysisLoading && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 16 }}>
                  <div style={{ width: 40, height: 40, border: `3px solid ${BRAND_PURPLE}40`, borderTop: `3px solid ${BRAND_PURPLE}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Analyzing team…</div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
              {activeTab === 0 && !teamAnalysisLoading && (
                <ExecutiveSummary data={td?.executiveSummary || null} onEmployeeClick={makeClickHandler(TABS.DIR)} onTabClick={setActiveTab} />
              )}

              {/* Tab 1: Technical Coverage */}
              {activeTab === 1 && (
                <TechnicalCoverageTabContent
                  components={components}
                  teamSynergy={td?.teamSynergy}
                  onEmployeeClick={makeClickHandler(TABS.SKILLS)}
                />
              )}

              {/* Tab 2: Team Chemistry */}
              {activeTab === 2 && (
                <Box>

                  {/* Chemistry Composite Score — live data */}
                  <Box sx={{ mb: 3 }}>
                    <ChemistryCompositeScore data={td?.chemistryComposite} />
                  </Box>

                  {/* Collaboration Network — live data */}
                  <Box sx={{ mb: 3 }}>
                    <CollaborationNetwork data={td?.collaborationNetwork} onEmployeeClick={makeClickHandler(TABS.ENGAGEMENTS)} />
                  </Box>

                  {/* DISC Engagement Fit — live data */}
                  <Box sx={{ mb: 3 }}>
                    <DISCEngagementFit data={td?.discFit} onEmployeeClick={makeClickHandler(TABS.PERSONAL)} />
                  </Box>

                  {/* StrengthsFinder Alignment — live data */}
                  <Box sx={{ mb: 3 }}>
                    <StrengthsFinderAlignment data={td?.strengthsAlignment} onEmployeeClick={makeClickHandler(TABS.PERSONAL)} />
                  </Box>



                  {/* DIR moved to Experience tab (C5) */}
                </Box>
              )}

              {/* Tab 4: Availability Timing */}
              {activeTab === 4 && (
                <Box>
                  {/* AvailabilityView — live data */}
                  <AvailabilityView data={td?.availabilityView} onEmployeeClick={makeClickHandler(TABS.TIMESHEETS)} />

                  {/* Legacy Timing Score Overview — hidden */}
                </Box>
              )}

              {/* Tab 5: Experience */}
              {activeTab === 5 && (
                <Box>
                  {/* DIR Score Analysis — live data */}
                  <Box sx={{ mb: 3 }}>
                    <DIRPlusMinusChart data={augmentedDirChart} onEmployeeClick={makeClickHandler(TABS.DIR)} />
                  </Box>


                  {/* Combined Experience Overview — live data */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Team Experience Overview
                      </Typography>
                      <Tooltip title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>How Experience Scores are Calculated:</Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, fontWeight: 'bold' }}>DIR Track Record (30%):</Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, ml: 2, fontSize: '0.7rem' }}>
                            Delivery Impact Rating — above/below average delivery history
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, fontWeight: 'bold' }}>Role Experience (25%):</Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, ml: 2, fontSize: '0.7rem' }}>
                            Number of projects in similar roles (40+ projects = 10/10)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, fontWeight: 'bold' }}>Industry Experience (20%):</Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, ml: 2, fontSize: '0.7rem' }}>
                            Total projects across all clients (20+ projects = 10/10)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, fontWeight: 'bold' }}>Client Experience (15%):</Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, ml: 2, fontSize: '0.7rem' }}>
                            Projects with this specific client (4+ projects = 10/10)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mb: 0.5, fontWeight: 'bold' }}>Role Versatility (10%):</Typography>
                          <Typography variant="caption" component="div" sx={{ ml: 2, fontSize: '0.7rem' }}>
                            Number of distinct roles held (5+ roles = 10/10)
                          </Typography>
                        </Box>
                      } arrow placement="right">
                        <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 2 }}>
                      Score = DIR (30%) + Role Experience (25%) + Industry (20%) + Client (15%) + Versatility (10%)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: getScoreColor(components.experience_balance?.average_role_experience_score || 0) }}>
                          {(components.experience_balance?.average_role_experience_score || 0).toFixed(1)}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>/</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: getScoreColor(components.industry_client_experience?.industry_average || 0) }}>
                          {(components.industry_client_experience?.industry_average || 0).toFixed(1)}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>/</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: (components.industry_client_experience?.client_average || 0) > 0 ? COLOR_GOOD : TEXT_DISABLED }}>
                          {(components.industry_client_experience?.client_average || 0).toFixed(1)}
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        {[
                          { label: 'Role Exp', value: components.experience_balance?.average_role_experience_score || 0, color: getScoreColor(components.experience_balance?.average_role_experience_score || 0) },
                          { label: 'Industry', value: components.industry_client_experience?.industry_average || 0, color: getScoreColor(components.industry_client_experience?.industry_average || 0) },
                          { label: 'Client',   value: components.industry_client_experience?.client_average || 0,   color: (components.industry_client_experience?.client_average || 0) > 0 ? COLOR_GOOD : TEXT_DISABLED },
                        ].map(({ label, value, color }) => (
                          <Box key={label} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: label === 'Role Exp' ? 0 : 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color, fontWeight: 'bold', minWidth: '60px' }}>
                              {label}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(value * 10, 100)}
                              sx={{ height: 4, borderRadius: 2, flexGrow: 1, bgcolor: BORDER_SECTION, '& .MuiLinearProgress-bar': { backgroundColor: color } }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Team experience: Role-specific (0-10) / Industry (0-10) / Client (0-10)
                    </Typography>
                  </Box>

                  {/* Team Member Experience Cards - Unified View */}
                  {components.experience_balance?.team_role_experience?.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Team Member Experience Profiles
                      </Typography>
                      <Grid container spacing={2}>
                        {components.experience_balance.team_role_experience.map((roleMember, index) => {
                          // Find matching industry/client data
                          const industryMember = components.industry_client_experience?.team_analysis?.find(
                            m => m.name === roleMember.name || m.employee_id === roleMember.employee_id
                          );

                          return (
                            <Grid item xs={12} md={6} key={index}>
                              <Card sx={{ p: 2, bgcolor: BG_ELEVATED, border: `1px solid ${BORDER_CARD}`, height: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                      <EmployeeNameLink employee={roleMember} tab={TABS.DIR} />
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {roleMember.assigned_role}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: getScoreColor(roleMember.role_experience_score) }}>
                                    {roleMember.role_experience_score.toFixed(1)}
                                  </Typography>
                                </Box>

                                {/* Role-Specific Experience */}
                                <Box sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                      Role Experience (6 pts)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      {roleMember.projects_in_similar_roles != null
                                        ? `${roleMember.projects_in_similar_roles} projects · ${roleMember.role_specific_score.toFixed(1)} pts`
                                        : `${roleMember.role_specific_score.toFixed(1)} pts`}
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(roleMember.role_specific_score / 6) * 100}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: BORDER_SECTION,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: (roleMember.role_specific_score / 6) < 0.3 ? COLOR_POOR :
                                                       (roleMember.role_specific_score / 6) < 0.8 ? COLOR_WARN : COLOR_GOOD
                                      }
                                    }}
                                  />
                                </Box>

                                {/* Role Versatility */}
                                <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${BORDER_SECTION}` }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                      Role Versatility (2 pts)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      {roleMember.role_versatility_score.toFixed(1)} pts
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(roleMember.role_versatility_score / 2) * 100}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: BORDER_SECTION,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: (roleMember.role_versatility_score / 2) < 0.3 ? COLOR_POOR :
                                                       (roleMember.role_versatility_score / 2) < 0.8 ? COLOR_WARN : COLOR_GOOD
                                      }
                                    }}
                                  />
                                </Box>

                                {/* Industry Experience */}
                                <Box sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                      Industry Experience (10 pts)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      {industryMember?.industry_score !== null && industryMember?.industry_score !== undefined
                                        ? (industryMember.projects_in_industry != null
                                            ? `${industryMember.projects_in_industry} projects · ${industryMember.industry_score.toFixed(1)} pts`
                                            : `${industryMember.industry_score.toFixed(1)} pts`)
                                        : 'No data'}
                                    </Typography>
                                  </Box>
                                  {industryMember?.industry_score !== null && industryMember?.industry_score !== undefined ? (
                                    <LinearProgress
                                      variant="determinate"
                                      value={industryMember.industry_score * 10}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: BORDER_SECTION,
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: (industryMember.industry_score / 10) < 0.3 ? COLOR_POOR :
                                                         (industryMember.industry_score / 10) < 0.8 ? COLOR_WARN : COLOR_GOOD
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                      No industry data
                                    </Typography>
                                  )}
                                </Box>

                                {/* Client Experience */}
                                <Box sx={{ mb: 0 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                      Client Experience (10 pts)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      {industryMember?.client_score !== null && industryMember?.client_score !== undefined
                                        ? (industryMember.projects_at_client != null
                                            ? `${industryMember.projects_at_client} projects · ${industryMember.client_score.toFixed(1)} pts`
                                            : `${industryMember.client_score.toFixed(1)} pts`)
                                        : 'No data'}
                                    </Typography>
                                  </Box>
                                  {industryMember?.client_score !== null && industryMember?.client_score !== undefined ? (
                                    <LinearProgress
                                      variant="determinate"
                                      value={industryMember.client_score * 10}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: BORDER_SECTION,
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: COLOR_GOOD
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                      No client data
                                    </Typography>
                                  )}
                                  {/* Client experience highlight */}
                                  {industryMember?.client_score > 0 && (
                                    <Box sx={{ mt: 1, px: 1, py: 0.5, bgcolor: `${COLOR_GOOD}12`, borderRadius: 1, border: `1px solid ${COLOR_GOOD}33` }}>
                                      <Typography variant="caption" sx={{ color: COLOR_GOOD, fontWeight: 'bold', fontSize: '0.65rem' }}>
                                        ★ {industryMember.client_score >= 7 ? 'Strong' : 'Has'} client experience — repeat-business advantage
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* DIR & CSAT performance metrics */}
                                {(roleMember.dir_score != null || roleMember.csat_score != null) && (
                                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER_SECTION}` }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                      Performance Metrics
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {roleMember.dir_score != null && (
                                        <Chip
                                          label={`DIR: ${roleMember.dir_score.toFixed(2)}`}
                                          size="small"
                                          sx={{ fontSize: '0.65rem', height: 20 }}
                                        />
                                      )}
                                      {roleMember.dir_score_operational != null && (
                                        <Chip
                                          label={`DIR Ops: ${roleMember.dir_score_operational.toFixed(2)}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20 }}
                                        />
                                      )}
                                      {roleMember.csat_score != null && (
                                        <Chip
                                          label={`CSAT: ${typeof roleMember.csat_score === 'number' ? roleMember.csat_score.toFixed(1) : roleMember.csat_score}`}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20 }}
                                        />
                                      )}
                                      {roleMember.projects_in_similar_roles != null && (
                                        <Chip
                                          label={`${roleMember.projects_in_similar_roles} similar projects`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20, color: 'text.secondary' }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}

                </Box>
              )}

              {/* Tab 3: Financial Analysis */}
              {activeTab === 3 && (
                <Box>
                  <FinancialView data={td?.financialView} onEmployeeClick={makeClickHandler(TABS.COMPENSATION)} />
                </Box>
              )}

              {/* Risk Assessment Tab — removed (G5): risk surfaces in Mitigation Action Plan */}
              {activeTab === 99 && (
                <Box>
                  {/* Risk Score Gauge */}
                  <Box sx={{ mb: 4, p: 3, bgcolor: (() => {
                    const riskScore = components.risk_assessment?.risk_score || 0;
                    if (riskScore < 2.5) return `${COLOR_GOOD}0D`;
                    if (riskScore < 5.0) return `${COLOR_WARN}0D`;
                    return `${COLOR_POOR}0D`;
                  })(), borderRadius: 2, border: (() => {
                    const riskScore = components.risk_assessment?.risk_score || 0;
                    if (riskScore < 2.5) return `2px solid ${COLOR_GOOD}4D`;
                    if (riskScore < 5.0) return `2px solid ${COLOR_WARN}4D`;
                    return `2px solid ${COLOR_POOR}4D`;
                  })() }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: (() => {
                        const riskScore = components.risk_assessment?.risk_score || 0;
                        if (riskScore < 2.5) return COLOR_GOOD;
                        if (riskScore < 5.0) return COLOR_WARN;
                        return COLOR_POOR;
                      })() }}>
                        {(components.risk_assessment?.risk_score || 0).toFixed(1)}/10
                      </Typography>
                      <Chip
                        label={components.risk_assessment?.risk_level?.toUpperCase() || 'UNKNOWN'}
                        color={(() => {
                          const level = components.risk_assessment?.risk_level || 'medium';
                          if (level === 'low') return 'success';
                          if (level === 'medium') return 'warning';
                          return 'error';
                        })()}
                        sx={{ fontSize: '1rem', fontWeight: 'bold', height: 32 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Project: {components.risk_assessment?.project_complexity?.classification || 'UNKNOWN'} • Team: {components.risk_assessment?.team_size || 0} members
                      </Typography>
                    </Box>
                  </Box>

                  {/* Risk Breakdown - 5 Categories */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Risk Breakdown
                      </Typography>
                      <Tooltip title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Risk Scoring System (0-10 scale):</Typography>
                          <Typography variant="caption" component="div">• Skills Coverage: Missing skills, single points of failure</Typography>
                          <Typography variant="caption" component="div">• Capacity: CSOT utilization, overloaded members</Typography>
                          <Typography variant="caption" component="div">• Experience: Junior/senior balance vs complexity</Typography>
                          <Typography variant="caption" component="div">• Track Record: CSAT and DIR performance</Typography>
                          <Typography variant="caption" component="div">• Team Chemistry: DISC conflicts</Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>Lower scores = lower risk</Typography>
                        </Box>
                      } arrow placement="right">
                        <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>

                    {components.risk_assessment?.risk_breakdown ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Skills Coverage */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              Skills Coverage
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {(components.risk_assessment.risk_breakdown.skills_coverage || 0).toFixed(1)}/10
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(components.risk_assessment.risk_breakdown.skills_coverage / 10) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: components.risk_assessment.risk_breakdown.skills_coverage < 3.3 ? COLOR_GOOD : components.risk_assessment.risk_breakdown.skills_coverage < 6.7 ? COLOR_WARN : COLOR_POOR
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            {(() => {
                              const risks = components.risk_assessment?.identified_risks || [];
                              const skillsRisks = risks.filter(r => r.toLowerCase().includes('skill') || r.toLowerCase().includes('point of failure'));
                              if (skillsRisks.length > 0) return skillsRisks[0];
                              if (components.risk_assessment.risk_breakdown.skills_coverage === 0) return 'All critical skills covered';
                              return 'Minor coverage concerns (acceptable for team size)';
                            })()}
                          </Typography>
                        </Box>

                        {/* Capacity */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              Capacity & Availability
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {(components.risk_assessment.risk_breakdown.capacity || 0).toFixed(1)}/10
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(components.risk_assessment.risk_breakdown.capacity / 10) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: components.risk_assessment.risk_breakdown.capacity < 3.3 ? COLOR_GOOD : components.risk_assessment.risk_breakdown.capacity < 6.7 ? COLOR_WARN : COLOR_POOR
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            {(() => {
                              const risks = components.risk_assessment?.identified_risks || [];
                              const capacityRisks = risks.filter(r => r.toLowerCase().includes('overloaded') || r.toLowerCase().includes('utilization') || r.toLowerCase().includes('csot'));
                              if (capacityRisks.length > 0) return capacityRisks[0];
                              if (components.risk_assessment.risk_breakdown.capacity === 0) return 'Team has healthy capacity levels';
                              return 'Some capacity concerns';
                            })()}
                          </Typography>
                        </Box>

                        {/* Experience */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              Experience vs Complexity
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {(components.risk_assessment.risk_breakdown.experience || 0).toFixed(1)}/10
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(components.risk_assessment.risk_breakdown.experience / 10) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: components.risk_assessment.risk_breakdown.experience < 3.3 ? COLOR_GOOD : components.risk_assessment.risk_breakdown.experience < 6.7 ? COLOR_WARN : COLOR_POOR
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            {(() => {
                              const risks = components.risk_assessment?.identified_risks || [];
                              const expRisks = risks.filter(r => r.toLowerCase().includes('senior') || r.toLowerCase().includes('junior') || r.toLowerCase().includes('experience') || r.toLowerCase().includes('expertise'));
                              if (expRisks.length > 0) return expRisks[0];
                              if (components.risk_assessment.risk_breakdown.experience === 0) return 'Experience level matches project complexity';
                              return 'Some experience concerns';
                            })()}
                          </Typography>
                        </Box>

                        {/* Track Record */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              Historical Performance
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {(components.risk_assessment.risk_breakdown.track_record || 0).toFixed(1)}/10
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(components.risk_assessment.risk_breakdown.track_record / 10) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: components.risk_assessment.risk_breakdown.track_record < 3.3 ? COLOR_GOOD : components.risk_assessment.risk_breakdown.track_record < 6.7 ? COLOR_WARN : COLOR_POOR
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            {(() => {
                              const risks = components.risk_assessment?.identified_risks || [];
                              const perfRisks = risks.filter(r => r.toLowerCase().includes('csat') || r.toLowerCase().includes('dir') || r.toLowerCase().includes('performance'));
                              if (perfRisks.length > 0) return perfRisks[0];
                              if (components.risk_assessment.risk_breakdown.track_record === 0) return 'Team has strong performance history';
                              return 'Some performance concerns';
                            })()}
                          </Typography>
                        </Box>

                        {/* Team Chemistry */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              Team Chemistry
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {(components.risk_assessment.risk_breakdown.team_chemistry || 0).toFixed(1)}/10
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(components.risk_assessment.risk_breakdown.team_chemistry / 10) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'rgba(0, 0, 0, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: components.risk_assessment.risk_breakdown.team_chemistry < 3.3 ? COLOR_GOOD : components.risk_assessment.risk_breakdown.team_chemistry < 6.7 ? COLOR_WARN : COLOR_POOR
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                            {(() => {
                              const risks = components.risk_assessment?.identified_risks || [];
                              const chemistryRisks = risks.filter(r => r.toLowerCase().includes('disc') || r.toLowerCase().includes('chemistry') || r.toLowerCase().includes('personality') || r.toLowerCase().includes('conflict'));
                              if (chemistryRisks.length > 0) return chemistryRisks[0];
                              if (components.risk_assessment.risk_breakdown.team_chemistry === 0) return 'Good team chemistry / no personality conflicts';
                              return 'Minor chemistry concerns';
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No risk breakdown available
                      </Typography>
                    )}
                  </Box>

                  {/* Identified Risks */}
                  {components.risk_assessment?.identified_risks?.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Identified Risks
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {components.risk_assessment.identified_risks.map((risk, index) => {
                          const isCritical = risk.startsWith('❌') || risk.startsWith('🚨');
                          const isWarning = risk.startsWith('⚠️');
                          const isInfo = risk.startsWith('ℹ️');

                          return (
                            <Alert
                              key={index}
                              severity={isCritical ? 'error' : isWarning ? 'warning' : 'info'}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {risk}
                            </Alert>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Project Complexity Factors */}
                  {components.risk_assessment?.project_complexity?.factors?.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Project Complexity Factors
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {components.risk_assessment.project_complexity.factors.map((factor, index) => (
                          <Chip
                            key={index}
                            label={factor}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Concierge Analysis */}
        <Grid item xs={12} md={4}>
          <ConciergeInsights data={td?.conciergeInsights || null} />
        </Grid>
      </Grid>

      {/* Full Analysis Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: `${BRAND_PURPLE}1A`,
          borderBottom: `1px solid ${BRAND_PURPLE}33`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
              <InsightsIcon sx={{ mr: 1, color: BRAND_PURPLE, verticalAlign: 'middle', fontSize: '1.2rem' }} />
              {staffingData?.name || staffingData?.opportunity_name || 'Team Analysis'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.25 }}>
              {[
                staffingData?.client_name || staffingData?.client,
                (teamAnalysisData?.executiveSummary?.team || []).map(m => m.name).join(', ')
              ].filter(Boolean).join(' · ')}
              {detailedAnalysisTimestamp ? ` · Generated ${new Date(detailedAnalysisTimestamp).toLocaleString()}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {detailedAnalysis && !isDetailedAnalysisLoading && (
              <Button
                startIcon={isDetailedAnalysisLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerateAnalysis}
                variant="outlined"
                size="small"
                disabled={isDetailedAnalysisLoading}
                sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
              >
                Regenerate
              </Button>
            )}
            {detailedAnalysis && !isDetailedAnalysisPending && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => generateDetailedAnalysisPDF(detailedAnalysis, staffingData, teamAnalysisData)}
                variant="outlined"
                size="small"
                sx={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE, '&:hover': { borderColor: BRAND_PURPLE, bgcolor: `${BRAND_PURPLE}0D` } }}
              >
                Download PDF
              </Button>
            )}
            <IconButton onClick={() => setIsModalOpen(false)} sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          <Box sx={{ minHeight: '200px' }}>
            {isDetailedAnalysisLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                <CircularProgress size={40} sx={{ color: BRAND_PURPLE }} />
                <Typography variant="body2" color="text.secondary">Generating analysis...</Typography>
              </Box>
            ) : detailedAnalysisError ? (
              <Box sx={{ p: 2, bgcolor: `${COLOR_POOR}14`, borderRadius: 1, border: `1px solid ${COLOR_POOR}4D` }}>
                <Typography variant="body2" sx={{ color: COLOR_POOR, fontWeight: 'bold', mb: 0.5 }}>Analysis failed</Typography>
                <Typography variant="body2" sx={{ color: COLOR_POOR, fontFamily: 'monospace', fontSize: '0.8rem' }}>{detailedAnalysisError}</Typography>
              </Box>
            ) : detailedAnalysis ? (
              renderStructuredAnalysis(detailedAnalysis)
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No analysis available. Click "View Detailed Analysis" to generate one.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Performance Prediction Info Dialog */}
      <Dialog
        open={performancePredictionInfoOpen}
        onClose={() => setPerformancePredictionInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: `${COLOR_GOOD}1A`, 
          borderBottom: `1px solid ${COLOR_GOOD}33`,
          display: 'flex',
          alignItems: 'center'
        }}>
          <PerformanceIcon sx={{ mr: 1, color: COLOR_GOOD }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Performance Prediction Calculation
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLOR_GOOD }}>
            What "Performance Prediction" Measures
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            The Performance Prediction shows the likelihood that this team will deliver a successful project 
            that satisfies the client, based on their historical performance and contextual experience factors.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: BRAND_PURPLE }}>
            📊 Enhanced Data Sources & Weights
          </Typography>
          
          <Box sx={{ mb: 3, pl: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>• CSAT Scores</strong> (Client Satisfaction) - 40% weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              How satisfied clients are with individual team members (1-5 scale)
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>• DIR Scores</strong> (Delivery Impact Rating) - 30% weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              Track record of successful project delivery outcomes (-2 to +2 scale, like +/- in hockey/basketball)
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>• Industry Experience</strong> - 15% weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              Team's collective experience in the client's industry and domain
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>• Client Experience</strong> - 10% weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              Previous successful engagements with this specific client
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>• Team Collaboration History</strong> - 5% weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
              How well team members have worked together on previous projects
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLOR_WARN }}>
            🔢 Enhanced Calculation Method
          </Typography>
          
          <Box sx={{ mb: 3, pl: 2, bgcolor: `${COLOR_WARN}0D`, p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
              <strong>Step 1:</strong> Collect individual performance metrics from each team member
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
              <strong>Step 2:</strong> Calculate contextual experience factors (industry, client, collaboration)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
              <strong>Step 3:</strong> Apply experience multipliers to base performance scores
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
              <strong>Step 4:</strong> Combine all factors with sophisticated weighting
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              <strong>Final:</strong> success_probability = (csat×0.4) + (dir×0.3) + (industry×0.15) + (client×0.1) + (collaboration×0.05)
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: BRAND_PURPLE }}>
            💡 Enhanced Example Calculation
          </Typography>
          
          <Box sx={{ mb: 3, pl: 2, bgcolor: `${BRAND_PURPLE}0D`, p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Healthcare project team of 3 members:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, pl: 1 }}>
              • Member A: CSAT 4.2, DIR 1.1, Healthcare exp: High
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, pl: 1 }}>
              • Member B: CSAT 3.8, DIR 0.3, Healthcare exp: Medium
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 1 }}>
              • Member C: CSAT 4.5, DIR -0.2, Healthcare exp: High
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Enhanced Calculation:</strong>
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Base CSAT Score = (4.2 + 3.8 + 4.5) / 3 = 4.17 → 8.34/10
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Base DIR Score = (1.1 + 0.3 + (-0.2)) / 3 = 0.4 → 6.0/10
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Industry Experience = 8.5/10 (strong healthcare background)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Client Experience = 7.0/10 (some prior work with client)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Collaboration History = 6.0/10 (limited prior teamwork)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 1 }}>
              Final = (8.34×0.4) + (6.0×0.3) + (8.5×0.15) + (7.0×0.1) + (6.0×0.05) = 7.41/10
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLOR_GOOD }}>
              <strong>Enhanced Success Probability = 74.1%</strong>
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: COLOR_POOR }}>
            ⚠️ Important Notes
          </Typography>
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>DIR measures delivery outcomes</strong>, not individual talent or capability
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>Not team chemistry</strong> - this is separate from personality compatibility
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>Not technical skills</strong> - this is about execution track record
            </Typography>
            <Typography variant="body2">
              • <strong>Confidence interval</strong> varies based on data completeness (±10-20% typical)
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: `${COLOR_GOOD}0D` }}>
          <Button 
            onClick={() => setPerformancePredictionInfoOpen(false)}
            variant="contained"
            sx={{ 
              bgcolor: COLOR_GOOD, 
              color: 'white',
              '&:hover': { 
                bgcolor: COLOR_GOOD,
                color: 'white'
              }
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Introduction Modal */}
      <ClientIntroductionModal
        open={clientIntroductionModalOpen}
        onClose={() => setClientIntroductionModalOpen(false)}
        teamOptimizationResult={currentOptimizationResult}
        projectContext={{
          project_name: staffingData?.projectName || staffingData?.name || 'Team Analysis Project',
          client_name: staffingData?.clientName || 'Client',
          opportunity_id: staffingData?.id || staffingData?.opportunity_id,
          close_date: staffingData?.estimatedCloseDate,
          start_date: staffingData?.start_date,
          win_probability: staffingData?.winProbability,
        }}
        clientContext={{
          client_name: staffingData?.clientName || 'Client',
          industry: staffingData?.industry || 'Professional Services',
          engagement_type: 'Team Analysis & Consulting'
        }}
        initialDocument={currentOptimizationResult?.client_introduction || null}
        onGenerated={(doc) => {
          // Merge client_introduction into optimizationResult so Workspace can save it
          if (onOptimizationUpdate) {
            const base = localOptimizationResult || optimizationResult || {};
            onOptimizationUpdate({ ...base, client_introduction: doc });
          }
        }}
      />
    </Box>
  );
};

TeamOptimizationDashboard.propTypes = {
  staffingData: PropTypes.object.isRequired,
  onOptimizationUpdate: PropTypes.func,
  optimizationGoal: PropTypes.string
};

export default TeamOptimizationDashboard;
